import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Plus, Search, X, Trash2, Edit3, Upload, Film, RefreshCw, PlayCircle } from "lucide-react";
import { toast } from "sonner";

const STATUS = [
  { v: "neu", l: "Neu (erscheint bald)" },
  { v: "demnaechst", l: "Demnächst" },
  { v: "heute", l: "Heute im Programm" },
  { v: "verfuegbar", l: "Aktuell verfügbar" },
  { v: "nicht_verfuegbar", l: "Aktuell nicht verfügbar (versteckt)" },
  { v: "archiviert", l: "Archiviert" },
];

const EMPTY = {
  title_de: "", title_original: "", description: "", poster_url: "", backdrop_url: "", logo_url: "",
  trailer_url: "", genre: "", duration_min: 120, year: null, rating: "FSK 12", language: "Deutsch",
  subtitles: "", director: "", cast: "", personal_rating: null, external_link: "", tmdb_id: null,
  status: "verfuegbar", is_public: true, release_date: "",
  playback_source: "", playback_kodi_path: "", playback_kodi_host: "", playback_streaming_url: "", playback_note: "",
};

const PLAYBACK_SOURCES = [
  { v: "", l: "— nicht festgelegt —" },
  { v: "kodi", l: "Kodi (JSON-RPC)" },
  { v: "plex", l: "Plex" },
  { v: "jellyfin", l: "Jellyfin" },
  { v: "appletv", l: "Apple TV 4K" },
  { v: "netflix", l: "Netflix" },
  { v: "disney", l: "Disney+" },
  { v: "prime", l: "Amazon Prime Video" },
  { v: "youtube", l: "YouTube" },
  { v: "bluray", l: "Blu-ray / Regal" },
  { v: "other", l: "Andere" },
];

export default function Movies() {
  const [movies, setMovies] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [tmdbOn, setTmdbOn] = useState(false);
  const [quickQ, setQuickQ] = useState("");
  const [quickResults, setQuickResults] = useState([]);
  const [quickBusy, setQuickBusy] = useState(false);

  const load = () => api.get("/movies").then((r) => setMovies(r.data));
  useEffect(() => { load(); api.get("/tmdb/status").then(r => setTmdbOn(r.data.configured)).catch(() => {}); }, []);

  const remove = async (id) => {
    if (!window.confirm("Diesen Film wirklich löschen?")) return;
    await api.delete(`/movies/${id}`);
    toast.success("Film gelöscht");
    load();
  };

  const refreshTmdb = async (m) => {
    if (!m.tmdb_id) { toast.error("Diesem Film ist keine TMDb-ID zugeordnet."); return; }
    try {
      await api.post(`/movies/${m.id}/refresh-tmdb`);
      toast.success(`„${m.title_de}" von TMDb aktualisiert (Darsteller · Regie · Beschreibung)`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "TMDb-Refresh fehlgeschlagen"); }
  };

  const quickSearch = async () => {
    if (!quickQ.trim()) return;
    setQuickBusy(true);
    try {
      const { data } = await api.get(`/tmdb/search`, { params: { q: quickQ } });
      setQuickResults(data.slice(0, 10));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setQuickBusy(false); }
  };

  const quickImport = async (tmdb_id) => {
    setQuickBusy(true);
    try {
      const { data } = await api.post(`/tmdb/import/${tmdb_id}`);
      toast.success(`„${data.title_de}" importiert`);
      setQuickResults([]);
      setQuickQ("");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setQuickBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="movies-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#A8A398]">Katalog</div>
          <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl">Filme</h1>
        </div>
        <button
          data-testid="movie-add-btn"
          onClick={() => { setEdit({ ...EMPTY }); setOpen(true); }}
          className="flex items-center gap-2 bg-[#D4AF37] text-black px-4 py-2 rounded-md text-sm uppercase tracking-widest hover:bg-[#EEDC82] transitions-safe"
        >
          <Plus size={16} /> Manuell anlegen
        </button>
      </div>

      {tmdbOn && (
        <div className="card-lux p-4" data-testid="movies-tmdb-quick">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] flex items-center gap-2 mb-3">
            <Search size={12} /> Film aus TMDb suchen &amp; sofort importieren
          </div>
          <div className="flex gap-2">
            <input value={quickQ} onChange={(e) => setQuickQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && quickSearch()}
              data-testid="movies-tmdb-input"
              placeholder="Filmtitel eingeben — Poster, Backdrop, Beschreibung, Regie, Cast, Trailer &amp; FSK werden automatisch geladen"
              className="flex-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#D4AF37] outline-none" />
            <button data-testid="movies-tmdb-search" onClick={quickSearch} disabled={quickBusy}
              className="bg-[#D4AF37] text-black px-5 py-2 rounded text-sm uppercase tracking-widest hover:bg-[#EEDC82] disabled:opacity-50">
              Suchen
            </button>
          </div>
          {quickResults.length > 0 && (
            <div className="mt-4 grid grid-cols-3 md:grid-cols-5 gap-3">
              {quickResults.map((r) => {
                const already = movies.some((m) => m.tmdb_id === r.tmdb_id);
                return (
                  <button key={r.tmdb_id} onClick={() => !already && quickImport(r.tmdb_id)} disabled={already || quickBusy}
                    data-testid={`movies-tmdb-pick-${r.tmdb_id}`}
                    className={`text-left bg-[#050505] border rounded overflow-hidden transitions-safe ${
                      already ? "border-[#D4AF37]/40 opacity-50 cursor-not-allowed" : "border-[#2A2A2A] hover:border-[#D4AF37]"
                    }`}
                    title={already ? "Bereits im Katalog" : "Zum Katalog hinzufügen"}
                  >
                    <div className="aspect-[2/3] bg-[#1A1A1A] relative">
                      {r.poster_url && <img src={r.poster_url} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }} className="w-full h-full object-cover" />}
                      {already && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-[9px] uppercase tracking-widest bg-[#D4AF37] text-black px-2 py-0.5 rounded-full">Schon im Katalog</span>
                        </div>
                      )}
                    </div>
                    <div className="p-2">
                      <div className="text-xs truncate">{r.title}</div>
                      <div className="text-[10px] text-[#A8A398]">{r.year} · {r.original_title}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
          <div className="text-[10px] text-[#575148] mt-3">Ein Klick auf ein Poster erzeugt automatisch einen kompletten Film-Eintrag inkl. aller Metadaten und Bilder.</div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
        {movies.map((m) => (
          <div key={m.id} className="card-lux overflow-hidden group" data-testid={`movie-card-${m.id}`}>
            <div className="aspect-[2/3] bg-[#1A1A1A] relative">
              {m.poster_url ? (
                <img src={m.poster_url} alt={m.title_de} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[#A8A398]"><Film /></div>
              )}
              <div className="absolute top-2 right-2 text-[9px] bg-black/70 border border-[#D4AF37]/40 text-[#D4AF37] uppercase tracking-widest px-2 py-0.5 rounded-full">
                {STATUS.find(s => s.v === m.status)?.l || m.status}
              </div>
            </div>
            <div className="p-3">
              <div className="font-heading text-lg leading-tight truncate">{m.title_de}</div>
              <div className="text-xs text-[#A8A398] truncate">{m.title_original || " "}</div>
              <div className="text-xs text-[#A8A398] mt-1">{m.year || "—"} · {m.duration_min} Min</div>
              <div className="flex gap-2 mt-3">
                <button onClick={() => { setEdit(m); setOpen(true); }} className="flex-1 text-xs py-1.5 border border-[#2A2A2A] rounded hover:border-[#D4AF37] transitions-safe">
                  <Edit3 size={12} className="inline mr-1" /> Bearbeiten
                </button>
                {m.playback_source && (
                  <button onClick={async () => {
                      try {
                        if (m.playback_source === "kodi") {
                          await api.post(`/movies/${m.id}/kodi-play`);
                          toast.success(`Kodi startet „${m.title_de}"`);
                        } else {
                          const { data } = await api.get(`/movies/${m.id}/playback-link`);
                          if (data.deep_link) { window.open(data.deep_link, "_blank", "noreferrer"); toast.success(`${data.source.toUpperCase()} geöffnet`); }
                          else toast.error("Kein Deep-Link verfügbar");
                        }
                      } catch (err) { toast.error(err.response?.data?.detail || "Playback fehlgeschlagen"); }
                    }} title={`Abspielen (${m.playback_source})`} data-testid={`movie-play-${m.id}`}
                    className="text-xs px-2 py-1.5 border border-[#8FBC8F]/60 text-[#8FBC8F] rounded hover:bg-[#8FBC8F]/10 transitions-safe">
                    <PlayCircle size={14} />
                  </button>
                )}
                <button onClick={() => remove(m.id)} className="text-xs px-2 py-1.5 border border-[#2A2A2A] rounded hover:border-[#7A1921] hover:text-[#EE9CA1] transitions-safe">
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {open && (
        <MovieDialog
          initial={edit}
          tmdbOn={tmdbOn}
          onClose={() => setOpen(false)}
          onSaved={() => { setOpen(false); load(); }}
        />
      )}
    </div>
  );
}

function MovieDialog({ initial, tmdbOn, onClose, onSaved }) {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [posters, setPosters] = useState([]);
  const [tmdbBusy, setTmdbBusy] = useState(false);
  const isNew = !form.id;

  const upd = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const doSearch = async () => {
    if (!search.trim()) return;
    setTmdbBusy(true);
    try {
      const { data } = await api.get(`/tmdb/search`, { params: { q: search } });
      setResults(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setTmdbBusy(false);
    }
  };

  const pickTmdb = async (id) => {
    setTmdbBusy(true);
    try {
      const { data } = await api.get(`/tmdb/movie/${id}`);
      setForm((f) => ({
        ...f,
        title_de: data.title_de || f.title_de,
        title_original: data.title_original,
        description: data.description,
        poster_url: data.poster_url,
        backdrop_url: data.backdrop_url,
        logo_url: data.logo_url,
        trailer_url: data.trailer_url,
        genre: data.genre,
        duration_min: data.duration_min || 120,
        year: data.year,
        rating: data.rating || f.rating,
        language: data.language,
        cast: data.cast,
        director: data.director,
        external_link: data.external_link,
        tmdb_id: data.tmdb_id,
      }));
      setPosters(data.posters || []);
      setResults([]);
      toast.success("Filmdaten übernommen");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setTmdbBusy(false);
    }
  };

  const uploadImage = async (field, file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
    upd(field)(`${process.env.REACT_APP_BACKEND_URL}${data.url}`);
    toast.success("Datei hochgeladen");
  };

  const save = async () => {
    setBusy(true);
    try {
      if (isNew) await api.post("/movies", form);
      else await api.patch(`/movies/${form.id}`, form);
      toast.success("Gespeichert");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm p-4 overflow-y-auto flex items-start justify-center">
      <div className="w-full max-w-5xl bg-[#0A0A0A] border border-[#2A2A2A] rounded-xl my-3 sm:my-6" data-testid="movie-dialog">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#1A1A1A]">
          <div className="font-heading text-2xl">{isNew ? "Neuer Film" : "Film bearbeiten"}</div>
          <button onClick={onClose} className="text-[#A8A398] hover:text-[#F5F0E6]"><X /></button>
        </div>
        <div className="p-6 space-y-6">
          {isNew && (
            <div className="card-lux p-4">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mb-2">TMDb-Suche</div>
              {!tmdbOn ? (
                <div className="text-xs text-[#A8A398]">
                  TMDb ist nicht konfiguriert. Bitte <code>TMDB_API_KEY</code> in der Backend-Umgebung setzen. Filme können weiterhin manuell angelegt werden.
                </div>
              ) : (
                <div className="flex gap-2">
                  <input
                    data-testid="tmdb-search-input"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && doSearch()}
                    placeholder="Filmtitel eingeben …"
                    className="flex-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#D4AF37] outline-none"
                  />
                  <button data-testid="tmdb-search-btn" onClick={doSearch} disabled={tmdbBusy} className="bg-[#D4AF37] text-black px-4 py-2 rounded text-sm uppercase tracking-widest">
                    <Search size={14} className="inline mr-1" /> Suchen
                  </button>
                </div>
              )}
              {results.length > 0 && (
                <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                  {results.slice(0, 8).map((r) => (
                    <button key={r.tmdb_id} onClick={() => pickTmdb(r.tmdb_id)} data-testid={`tmdb-result-${r.tmdb_id}`}
                      className="text-left bg-[#050505] border border-[#2A2A2A] rounded overflow-hidden hover:border-[#D4AF37] transitions-safe">
                      <div className="aspect-[2/3] bg-[#1A1A1A]">
                        {r.poster_url && <img src={r.poster_url} alt={r.title} className="w-full h-full object-cover" />}
                      </div>
                      <div className="p-2">
                        <div className="text-sm truncate">{r.title}</div>
                        <div className="text-[10px] text-[#A8A398]">{r.year} · {r.original_title}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {posters.length > 1 && (
                <div className="mt-4">
                  <div className="text-[10px] uppercase tracking-widest text-[#A8A398] mb-2">Filmplakat wählen</div>
                  <div className="flex gap-2 overflow-x-auto no-scrollbar">
                    {posters.map((p, i) => (
                      <button key={i} onClick={() => upd("poster_url")(p.full)}
                        className={`shrink-0 w-24 aspect-[2/3] rounded overflow-hidden border transitions-safe ${form.poster_url === p.full ? "border-[#D4AF37]" : "border-[#2A2A2A]"}`}>
                        <img src={p.url} alt="" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Deutscher Titel" value={form.title_de} onChange={upd("title_de")} testid="movie-title-de" />
            <Field label="Originaltitel" value={form.title_original} onChange={upd("title_original")} />
            <Field label="Genre" value={form.genre} onChange={upd("genre")} />
            <Field label="Laufzeit (Min)" type="number" value={form.duration_min} onChange={(v) => upd("duration_min")(parseInt(v || 0))} />
            <Field label="Jahr" type="number" value={form.year || ""} onChange={(v) => upd("year")(parseInt(v || 0) || null)} />
            <Field label="Altersfreigabe" value={form.rating} onChange={upd("rating")} />
            <Field label="Sprache" value={form.language} onChange={upd("language")} />
            <Field label="Untertitel" value={form.subtitles} onChange={upd("subtitles")} />
            <Field label="Regisseur" value={form.director} onChange={upd("director")} />
            <Field label="Darsteller" value={form.cast} onChange={upd("cast")} />
            <Field label="Trailer-Link" value={form.trailer_url} onChange={upd("trailer_url")} />
            <Field label="Externer Filmlink" value={form.external_link} onChange={upd("external_link")} />
            <Field label="Persönliche Bewertung (0–10)" type="number" value={form.personal_rating || ""} onChange={(v) => upd("personal_rating")(parseFloat(v || 0) || null)} />
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Abspielquelle</label>
              <select value={form.playback_source || ""} onChange={(e) => upd("playback_source")(e.target.value)}
                data-testid="movie-playback-source"
                className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#D4AF37] outline-none">
                {PLAYBACK_SOURCES.map((p) => <option key={p.v} value={p.v}>{p.l}</option>)}
              </select>
            </div>
            {form.playback_source === "kodi" ? (
              <>
                <Field label="Kodi Host (http://…:8080)" value={form.playback_kodi_host} onChange={upd("playback_kodi_host")} testid="movie-playback-kodi-host" />
                <Field label="Kodi Datei-Pfad" value={form.playback_kodi_path} onChange={upd("playback_kodi_path")} testid="movie-playback-kodi-path" />
              </>
            ) : ["netflix","disney","prime","appletv","plex","jellyfin","youtube","other"].includes(form.playback_source || "") ? (
              <Field label={`Direkter Link (optional, sonst Auto-Suche)`} value={form.playback_streaming_url} onChange={upd("playback_streaming_url")} testid="movie-playback-url" />
            ) : null}
            {form.playback_source ? (
              <Field label="Notiz (z. B. Regalfach, Untertitel-Track)" value={form.playback_note} onChange={upd("playback_note")} />
            ) : null}
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Status</label>
              <select value={form.status} onChange={(e) => upd("status")(e.target.value)}
                className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#D4AF37] outline-none">
                {STATUS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
              </select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 cursor-pointer py-2">
                <input type="checkbox" checked={form.is_public !== false} onChange={(e) => upd("is_public")(e.target.checked)}
                  className="accent-[#D4AF37]" data-testid="movie-is-public" />
                <span className="text-sm text-[#F5F0E6]">Auf Startseite anzeigen</span>
              </label>
            </div>
            <Field label={'Erscheint am (optional, für Status "Neu")'} type="date" value={form.release_date || ""} onChange={upd("release_date")} testid="movie-release-date" />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Beschreibung</label>
            <textarea rows={4} value={form.description || ""} onChange={(e) => upd("description")(e.target.value)}
              className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#D4AF37] outline-none" />
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <ImageField label="Filmplakat" value={form.poster_url} onChange={upd("poster_url")} onUpload={(f) => uploadImage("poster_url", f)} />
            <ImageField label="Hintergrundbild" value={form.backdrop_url} onChange={upd("backdrop_url")} onUpload={(f) => uploadImage("backdrop_url", f)} />
            <ImageField label="Logo (optional)" value={form.logo_url} onChange={upd("logo_url")} onUpload={(f) => uploadImage("logo_url", f)} />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-[#1A1A1A] flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-[#A8A398] hover:text-[#F5F0E6]">Abbrechen</button>
          <button data-testid="movie-save-btn" onClick={save} disabled={busy}
            className="bg-[#D4AF37] text-black px-6 py-2 rounded-md text-sm uppercase tracking-widest hover:bg-[#EEDC82] transitions-safe disabled:opacity-50">
            {busy ? "Speichern …" : "Speichern"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">{label}</label>
      <input data-testid={testid} type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
        className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#D4AF37] outline-none" />
    </div>
  );
}
function ImageField({ label, value, onChange, onUpload }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">{label}</label>
      <input value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder="URL"
        className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#D4AF37] outline-none" />
      <label className="mt-2 flex items-center justify-center gap-2 py-2 border border-dashed border-[#2A2A2A] rounded cursor-pointer text-xs text-[#A8A398] hover:border-[#D4AF37] transitions-safe">
        <Upload size={12} /> Datei hochladen
        <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && onUpload(e.target.files[0])} />
      </label>
      {value && <img src={value} alt="" className="mt-2 w-full max-h-40 object-cover rounded" />}
    </div>
  );
}
